'use client';

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/app/providers/auth-provider';

const LS_KEY = 'food-finder-favorites';

function getLocalFavorites(): number[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch { return []; }
}

function setLocalFavorites(favs: number[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(favs));
}

type FavoritesContextType = {
  favorites: number[];
  toggleFavorite: (stt: number) => Promise<void>;
  isFavorite: (stt: number) => boolean;
  loading: boolean;
};

const FavoritesContext = createContext<FavoritesContextType>({
  favorites: [],
  toggleFavorite: async () => {},
  isFavorite: () => false,
  loading: true,
});

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      const supabase = createClient();
      if (!supabase) { setLoading(false); return; }

      supabase.from('favorites').select('restaurant_stt').eq('user_id', user.id).then(({ data }) => {
        const stts = (data || []).map((f: any) => f.restaurant_stt);
        setFavorites(stts);
        setLoading(false);

        // Merge localStorage favorites on first login
        const local = getLocalFavorites();
        if (local.length > 0) {
          const toMerge = local.filter((s) => !stts.includes(s));
          if (toMerge.length > 0) {
            const inserts = toMerge.map((stt) => ({ user_id: user.id, restaurant_stt: stt }));
            supabase.from('favorites').upsert(inserts, { onConflict: 'user_id,restaurant_stt' }).then(() => {
              setFavorites([...stts, ...toMerge]);
            });
          }
          localStorage.removeItem(LS_KEY);
        }
      });
    } else {
      setFavorites(getLocalFavorites());
      setLoading(false);
    }
  }, [user]);

  const toggleFavorite = useCallback(async (stt: number) => {
    const isFav = favorites.includes(stt);
    const updated = isFav ? favorites.filter((f) => f !== stt) : [...favorites, stt];
    setFavorites(updated);

    if (user) {
      const supabase = createClient();
      if (!supabase) return;
      if (isFav) {
        await supabase.from('favorites').delete().eq('user_id', user.id).eq('restaurant_stt', stt);
      } else {
        await supabase.from('favorites').insert({ user_id: user.id, restaurant_stt: stt });
      }
    } else {
      setLocalFavorites(updated);
    }
  }, [favorites, user]);

  const isFavorite = useCallback((stt: number) => favorites.includes(stt), [favorites]);

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite, isFavorite, loading }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  return useContext(FavoritesContext);
}
